import React, { useState } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircle2, Circle, GripVertical, ArrowUpRight, Trash2, MoreVertical } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const SubtaskRow = ({ sub, onComplete, onPromote, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sub.task_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style}
      className="flex items-center gap-2 rounded-lg py-1.5 px-2 bg-card border border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]"
      data-testid={`subtask-row-${sub.task_id}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-[hsl(var(--muted-foreground))] touch-none" aria-label="Drag to reorder" data-testid={`subtask-drag-${sub.task_id}`}>
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <button onClick={() => onComplete(sub)} data-testid={`subtask-complete-${sub.task_id}`}>
        {sub.status === 'done'
          ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
          : <Circle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />}
      </button>
      <span className={`text-sm flex-1 ${sub.status === 'done' ? 'line-through text-[hsl(var(--muted-foreground))]' : ''}`}>{sub.title}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button className="text-[hsl(var(--muted-foreground))]" data-testid={`subtask-menu-${sub.task_id}`}><MoreVertical className="h-3.5 w-3.5" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onPromote(sub)} data-testid={`subtask-promote-${sub.task_id}`}><ArrowUpRight className="h-4 w-4 mr-2" /> Make standalone</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDelete(sub)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const PromoteZone = ({ active }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'promote-zone' });
  if (!active) return null;
  return (
    <div ref={setNodeRef} data-testid="subtask-promote-zone"
      className="mt-1.5 rounded-lg border-2 border-dashed p-2.5 text-center text-xs flex items-center justify-center gap-1.5 transition-colors"
      style={{
        borderColor: isOver ? 'hsl(var(--primary))' : 'hsl(var(--border))',
        color: isOver ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
        background: isOver ? 'hsl(var(--primary) / 0.08)' : 'transparent',
      }}>
      <ArrowUpRight className="h-3.5 w-3.5" /> Drop here to make it its own task
    </div>
  );
};

export const SubtaskList = ({ subs, onReorder, onComplete, onPromote, onDelete }) => {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const ids = subs.map((s) => s.task_id);

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    if (over.id === 'promote-zone') {
      const sub = subs.find((s) => s.task_id === active.id);
      if (sub) onPromote(sub);
      return;
    }
    if (active.id !== over.id) {
      const oldI = ids.indexOf(active.id);
      const newI = ids.indexOf(over.id);
      if (oldI >= 0 && newI >= 0) onReorder(arrayMove(ids, oldI, newI));
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragStart={({ active }) => setActiveId(active.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {subs.map((sub) => (
            <SubtaskRow key={sub.task_id} sub={sub} onComplete={onComplete} onPromote={onPromote} onDelete={onDelete} />
          ))}
        </div>
      </SortableContext>
      <PromoteZone active={activeId} />
    </DndContext>
  );
};

export default SubtaskList;
